import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, eur, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Clock, Check, ArrowLeft } from "lucide-react";

export function ServicesList() {
  const [services, setServices] = useState([]);
  useEffect(() => { api.get("/services").then((r) => setServices(r.data)); }, []);
  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="services-page">
      <div className="max-w-3xl">
        <div className="overline text-[#B97A48] mb-4">I servizi</div>
        <h1 className="font-serif text-5xl md:text-6xl leading-[1.05]">
          Quattro interventi,<br /><span className="italic">l'arte del restauro</span>.
        </h1>
        <p className="mt-6 text-[#5C5A56] text-lg leading-relaxed max-w-2xl">
          Solo calzature di lusso. Risuolatura, trattamento camoscio, pulizia pelle.
          Ogni paio viene trattato con gli strumenti e le tecniche della tradizione
          marchigiana, nel laboratorio di Mastro Ciabattino Palombi.
        </p>
      </div>

      <div className="mt-16 grid md:grid-cols-2 gap-8">
        {services.map((s, idx) => (
          <Link to={`/servizi/${s.slug}`} key={s.id} className="card-luxury overflow-hidden flex flex-col fade-up" style={{ animationDelay: `${idx * 0.06}s` }} data-testid={`service-${s.slug}`}>
            <div className="aspect-[16/10] bg-[#F4F1EA] overflow-hidden">
              <img
                src={s.image_url}
                alt={s.name}
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
            <div className="p-8 flex-1 flex flex-col">
              <h3 className="font-serif text-3xl">{s.name}</h3>
              <p className="mt-3 text-[#5C5A56] leading-relaxed">{s.description}</p>
              <div className="mt-6 flex items-center justify-between pt-6 border-t border-[#E4DFD5]">
                <div className="flex items-center gap-2 text-sm text-[#5C5A56]">
                  <Clock size={14} strokeWidth={1.5} /> {s.duration_days} giorni
                </div>
                <div>
                  <span className="text-xs text-[#5C5A56] mr-2">da</span>
                  <span className="font-serif text-2xl text-[#B97A48]">{eur(s.price_from)}</span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => setService(r.data)).catch((e) => setErr(formatError(e)));
  }, [slug]);

  if (err) return <div className="max-w-3xl mx-auto px-6 py-20 text-[#9E413B]">{err}</div>;
  if (!service) return <div className="max-w-7xl mx-auto px-6 py-20">Caricamento…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-testid="service-detail">
      <Link to="/servizi" className="text-sm text-[#5C5A56] hover:text-[#1C1B1A] inline-flex items-center gap-1 mb-8"><ArrowLeft size={14} /> Tutti i servizi</Link>
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
        <div className="aspect-[4/5] bg-[#F4F1EA] overflow-hidden border border-[#E4DFD5]">
          <img
            src={service.image_url}
            alt={service.name}
            className="w-full h-full object-cover"
          />
        </div>
        <div>
          <div className="overline text-[#B97A48] mb-3">Servizio artigianale</div>
          <h1 className="font-serif text-5xl md:text-6xl leading-[1.02]">{service.name}</h1>
          <p className="mt-6 text-[#5C5A56] text-lg leading-relaxed">{service.description}</p>

          <div className="mt-10 grid grid-cols-2 gap-6 p-6 bg-[#F4F1EA] border border-[#E4DFD5]">
            <div>
              <div className="overline text-[#5C5A56] mb-1">Prezzo da</div>
              <div className="font-serif text-3xl text-[#B97A48]">{eur(service.price_from)}</div>
            </div>
            <div>
              <div className="overline text-[#5C5A56] mb-1">Tempo atelier</div>
              <div className="font-serif text-3xl">{service.duration_days} <span className="text-lg">giorni</span></div>
            </div>
          </div>

          <ul className="mt-8 space-y-3 text-[#1C1B1A]">
            {["Etichetta prepagata SDA o DHL inviata entro poche ore", "Diagnosi da artigiano senior", "Riconsegna entro 2–3 settimane", "Garanzia 90 giorni sull'intervento"].map((f, i) => (
              <li key={i} className="flex items-start gap-3 text-sm"><Check size={16} strokeWidth={2} className="text-[#4A6B53] mt-0.5" /> {f}</li>
            ))}
          </ul>

          <div className="mt-10">
            <Link to={`/prenota/${service.slug}`} className="btn-primary" data-testid="book-service-btn">
              Prenota ora
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function BookingPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [service, setService] = useState(null);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    brand: "", model: "", color: "", material: "", notes: "",
    full_name: "", street: "", city: "", zip: "", province: "", phone: "",
  });

  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => setService(r.data)).catch((e) => setErr(formatError(e)));
  }, [slug]);

  useEffect(() => {
    if (user === false) nav(`/accedi?redirect=/prenota/${slug}`);
    if (user && user.id && !form.full_name) setForm((f) => ({ ...f, full_name: user.name || "" }));
  }, [user, nav, slug, form.full_name]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!service) return;
    setSubmitting(true); setErr("");
    try {
      const address = {
        full_name: form.full_name, street: form.street, city: form.city,
        zip: form.zip, province: form.province, country: "IT", phone: form.phone,
      };
      const { data } = await api.post("/orders", {
        order_type: "service",
        items: [{ item_type: "service", item_id: service.id, name: service.name, price: service.price_from, quantity: 1 }],
        shoe_info: { brand: form.brand, model: form.model, color: form.color, material: form.material, notes: form.notes },
        pickup_address: address,
        shipping_address: address,
      });
      // Create checkout
      const { data: co } = await api.post("/payments/checkout", { order_id: data.id, origin_url: window.location.origin });
      window.location.href = co.url;
    } catch (ex) {
      setErr(formatError(ex));
      setSubmitting(false);
    }
  };

  if (!service) return <div className="max-w-7xl mx-auto px-6 py-20">Caricamento…</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-16" data-testid="booking-page">
      <Link to={`/servizi/${slug}`} className="text-sm text-[#5C5A56] hover:text-[#1C1B1A] inline-flex items-center gap-1 mb-8"><ArrowLeft size={14} /> Indietro</Link>
      <div className="overline text-[#B97A48] mb-3">Prenotazione</div>
      <h1 className="font-serif text-4xl md:text-5xl leading-tight">{service.name}</h1>
      <p className="mt-4 text-[#5C5A56]">Compila i dati per la spedizione. L'etichetta prepagata (SDA o DHL) ti verrà inviata via email entro poche ore con tutte le istruzioni.</p>

      <form onSubmit={submit} className="mt-12 grid md:grid-cols-2 gap-8">
        <div className="md:col-span-2">
          <div className="overline mb-4">Dettagli calzature</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input className="input-field" placeholder="Marca (es. Church's)" value={form.brand} onChange={update("brand")} data-testid="shoe-brand" />
            <input className="input-field" placeholder="Modello" value={form.model} onChange={update("model")} data-testid="shoe-model" />
            <input className="input-field" placeholder="Colore" value={form.color} onChange={update("color")} data-testid="shoe-color" />
            <input className="input-field" placeholder="Materiale (es. vitello, camoscio)" value={form.material} onChange={update("material")} data-testid="shoe-material" />
          </div>
          <textarea className="input-field mt-4" rows={3} placeholder="Note aggiuntive (punti delicati, richieste specifiche)" value={form.notes} onChange={update("notes")} data-testid="shoe-notes" />
        </div>

        <div className="md:col-span-2">
          <div className="overline mb-4">Indirizzo di ritiro</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input className="input-field" required placeholder="Nome e cognome" value={form.full_name} onChange={update("full_name")} data-testid="addr-name" />
            <input className="input-field" required placeholder="Telefono" value={form.phone} onChange={update("phone")} data-testid="addr-phone" />
            <input className="input-field sm:col-span-2" required placeholder="Via e numero civico" value={form.street} onChange={update("street")} data-testid="addr-street" />
            <input className="input-field" required placeholder="Città" value={form.city} onChange={update("city")} data-testid="addr-city" />
            <div className="grid grid-cols-2 gap-4">
              <input className="input-field" required placeholder="CAP" value={form.zip} onChange={update("zip")} data-testid="addr-zip" />
              <input className="input-field" required placeholder="Provincia" value={form.province} onChange={update("province")} maxLength={2} data-testid="addr-province" />
            </div>
          </div>
        </div>

        {err && <div className="md:col-span-2 p-4 bg-[#9E413B]/10 text-[#9E413B] text-sm border border-[#9E413B]/30" data-testid="booking-error">{err}</div>}

        <div className="md:col-span-2 flex items-center justify-between p-6 bg-[#F4F1EA] border border-[#E4DFD5]">
          <div>
            <div className="overline">Totale da versare</div>
            <div className="font-serif text-3xl text-[#B97A48]">{eur(service.price_from)}</div>
            <div className="text-xs text-[#5C5A56] mt-1">Pagamento sicuro online</div>
          </div>
          <button type="submit" disabled={submitting} className="btn-primary" data-testid="submit-booking">
            {submitting ? "Invio in corso…" : "Conferma e paga"}
          </button>
        </div>
      </form>
    </div>
  );
}
